/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import { useRouterState } from '@tanstack/react-router'
import type { ReactElement, ReactNode } from 'react'
import { forwardRef, useMemo } from 'react'

import { NavBadge } from '@/components/blocks/nav-badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

export interface BottomNavMenuActionProps {
  icon: ReactElement
  label: string
  badge?: number | boolean
  isActive?: boolean | ((location: { pathname: string }) => boolean)
  children: ReactNode
}

export const BottomNavMenuAction = forwardRef<HTMLButtonElement, BottomNavMenuActionProps>(
  function BottomNavMenuAction(props, ref) {
    const { icon, label, badge, isActive: isActiveProp = false, children } = props
    const routerState = useRouterState()
    const currentPath = routerState.location.pathname

    const active = useMemo(() => {
      if (typeof isActiveProp === 'function') {
        return isActiveProp({ pathname: currentPath })
      }
      return isActiveProp
    }, [isActiveProp, currentPath])

    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          className={cn(
            'flex w-full min-w-0 flex-1 flex-col items-center justify-center border-none bg-transparent px-1 py-1 transition-colors',
            active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
          )}
          ref={ref}
        >
          <div className="relative inline-flex [&_svg]:size-[22px]">
            {icon}
            <NavBadge badge={badge} size="md" />
          </div>
          <span
            className={cn(
              'mt-0.5 max-w-full truncate text-center text-[10px] leading-tight',
              active ? 'font-semibold' : 'font-medium'
            )}
          >
            {label}
          </span>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" sideOffset={8}>
          {children}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }
)

BottomNavMenuAction.displayName = 'BottomNavMenuAction'
